import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Receipt, Search, Loader2, RefreshCw, Sun, User, ExternalLink, CalendarDays, Archive, FileText, Calculator, FileDown } from "lucide-react";
import { toast } from "sonner";

const eur = (v) => (Number(v || 0)).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
const num = (v, d = 2) => (Number(v || 0)).toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: d });
const STAGE_LABELS = { lead: "Lead", profilazione: "Profilazione", consulenza: "Consulenza", preventivo: "Preventivo", contratto: "Contratto" };
const MAT_STATUS = { bozza: "Bozza", aperto: "Aperto", chiuso: "Chiuso" };

const statoColor = (s) => {
  const t = (s || "").toLowerCase();
  if (t.includes("approv") || t.includes("accett") || t.includes("chius") || t.includes("contratt")) return "#34D399";
  if (t.includes("rifiut") || t.includes("annull") || t.includes("perso")) return "#F87171";
  if (t.includes("invi") || t.includes("attesa") || t.includes("apert") || t.includes("preventivo")) return "#60A5FA";
  return "#FDD003";
};
function StatoBadge({ stato }) {
  const c = statoColor(stato);
  return <span className="inline-flex items-center gap-1.5 text-xs rounded-full px-2.5 py-0.5" style={{ background: `${c}1f`, color: c, border: `1px solid ${c}55` }}><span className="h-1.5 w-1.5 rounded-full" style={{ background: c }} />{stato || "—"}</span>;
}

/* ---------------- Vista 1: Archivio FIER ---------------- */
function ArchivioFier() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stati, setStati] = useState([]);
  const [q, setQ] = useState("");
  const [stato, setStato] = useState("__all__");
  const [loading, setLoading] = useState(true);
  const [importState, setImportState] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (stato !== "__all__") params.stato = stato;
      const { data } = await api.get("/fier-preventivi", { params });
      setItems(data.items || []); setTotal(data.total || 0); setStati(data.stati || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [q, stato]);
  useEffect(() => { load(); }, [load]);

  const pollStatus = useCallback(() => {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get("/fier-preventivi/import/status");
        setImportState(data);
        if (data.phase === "done" || data.phase === "error") {
          clearInterval(pollRef.current); pollRef.current = null;
          if (data.phase === "done") { toast.success(`Import completato: ${data.created} nuovi · ${data.updated} aggiornati`); load(); }
          else toast.error(`Import FIER: ${data.error || "errore"}`);
        }
      } catch { /* keep polling */ }
    }, 3000);
  }, [load]);
  useEffect(() => {
    api.get("/fier-preventivi/import/status").then(({ data }) => { setImportState(data); if (data?.phase === "running") pollStatus(); }).catch(() => {});
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [pollStatus]);

  const startImport = async () => {
    try { await api.post("/fier-preventivi/import"); setImportState({ phase: "running", done: 0, total: 0 }); toast.info("Import da FIER avviato: scraping profondo in corso."); pollStatus(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openDetail = async (id) => {
    setDetailLoading(true); setDetail({ id });
    try { const { data } = await api.get(`/fier-preventivi/${id}`); setDetail(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setDetail(null); }
    finally { setDetailLoading(false); }
  };
  const running = importState?.phase === "running";
  const pct = running && importState.total ? Math.round((importState.done / importState.total) * 100) : 0;

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <p className="text-xs text-muted-foreground">{total} preventivi storici importati da FIER (sola lettura)</p>
        <Button size="sm" onClick={startImport} disabled={running} data-testid="fier-prev-import-btn" className="font-semibold">
          {running ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />} Importa da FIER
        </Button>
      </div>
      {running && (
        <div className="mb-3" data-testid="fier-prev-import-progress">
          <div className="flex justify-between text-xs text-muted-foreground mb-1"><span>Scraping preventivi FIER…</span><span>{importState.done || 0}{importState.total ? ` / ${importState.total}` : ""} {importState.total ? `(${pct}%)` : ""}</span></div>
          <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden"><div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} /></div>
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap mb-4">
        <div className="relative flex-1 min-w-[220px]"><Search className="h-4 w-4 absolute left-3 top-2.5 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca cliente o numero…" className="pl-9" data-testid="fier-prev-search" /></div>
        <Select value={stato} onValueChange={setStato}>
          <SelectTrigger className="w-52" data-testid="fier-prev-stato-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="__all__">Tutti gli stati</SelectItem>{stati.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {loading ? <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        : items.length === 0 ? <div className="rounded-lg border border-dashed border-border py-16 text-center text-muted-foreground"><Receipt className="h-8 w-8 mx-auto mb-3 opacity-40" /><p className="text-sm">Nessun preventivo importato. Premi <b>“Importa da FIER”</b> per scaricare lo storico.</p></div>
        : (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {items.map((p) => (
              <Card key={p.id} onClick={() => openDetail(p.id)} className="rounded-lg border-border p-4 cursor-pointer hover:border-primary/50 hover:bg-muted/20 transition-colors" data-testid={`fier-prev-card-${p.id}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div><div className="text-xs text-muted-foreground uppercase tracking-widest">N. {p.progressivo || "—"}</div><div className="font-semibold leading-tight">{p.cliente_nome || p.cliente || "—"}</div>{p.cliente_tipo && <div className="text-xs text-muted-foreground">{p.cliente_tipo}</div>}</div>
                  <StatoBadge stato={p.stato} />
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                  <span className="inline-flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> {p.data || "—"}</span>
                  <span className="inline-flex items-center gap-1"><Sun className="h-3.5 w-3.5" /> {num(p.potenza_kwp, 2)} kWp</span>
                  {p.cliente_id && <span className="inline-flex items-center gap-1 text-primary"><User className="h-3.5 w-3.5" /> in Anagrafica</span>}
                </div>
                <div className="mt-3 pt-3 border-t border-border/50 flex items-center justify-between"><span className="text-xs text-muted-foreground">{p.n_prodotti || 0} prodotti · {p.n_voci || 0} voci</span><span className="font-semibold text-sm">{eur(p.totale_prodotti)}</span></div>
              </Card>
            ))}
          </div>
        )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="fier-prev-detail">
          {detailLoading || !detail?.progressivo ? <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div> : (
            <>
              <DialogHeader><DialogTitle className="flex items-center gap-2">Preventivo n. {detail.progressivo} <StatoBadge stato={detail.stato} /></DialogTitle><DialogDescription>{detail.documento || detail.cliente}</DialogDescription></DialogHeader>
              <div className="space-y-4">
                <div className="grid sm:grid-cols-4 gap-3 text-sm">
                  <div><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Cliente</div><div className="font-medium">{detail.cliente_nome || detail.cliente}</div></div>
                  <div><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Data</div><div className="font-medium">{detail.data || "—"}</div></div>
                  <div><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Potenza</div><div className="font-medium">{num(detail.potenza_kwp, 2)} kWp</div></div>
                  <div><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Installazione prevista</div><div className="font-medium">{detail.data_installazione || "—"}</div></div>
                </div>
                {detail.cliente_id && <Button variant="outline" size="sm" onClick={() => navigate(`/clienti/${detail.cliente_id}`)} data-testid="fier-prev-open-anagrafica"><ExternalLink className="h-4 w-4 mr-1.5" /> Apri scheda Anagrafica</Button>}
                {(detail.prodotti || []).length > 0 && (
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-primary mb-1.5">Prodotti / materiale</div>
                    <div className="overflow-x-auto rounded-lg border border-border"><table className="w-full text-sm">
                      <thead><tr className="text-xs uppercase text-muted-foreground bg-secondary/60 border-b border-border"><th className="text-left py-2 px-3">Codice</th><th className="text-left py-2 pr-2">Tipologia</th><th className="text-right py-2 px-1">Q.tà</th><th className="text-right py-2 px-1">kWp</th><th className="text-right py-2 px-1">Prezzo un.</th><th className="text-right py-2 px-3">Totale</th></tr></thead>
                      <tbody>{detail.prodotti.map((r, i) => <tr key={i} className="border-b border-border/40"><td className="py-1.5 px-3">{r.nome || r.codice}</td><td className="py-1.5 pr-2 text-muted-foreground">{r.tipologia}</td><td className="py-1.5 px-1 text-right">{num(r.quantita, 2)}</td><td className="py-1.5 px-1 text-right">{num(r.kwp, 2)}</td><td className="py-1.5 px-1 text-right">{eur(r.prezzo_unit)}</td><td className="py-1.5 px-3 text-right font-medium">{eur(r.prezzo_totale)}</td></tr>)}</tbody>
                    </table></div>
                    <div className="text-right text-sm font-semibold mt-1.5">Totale prodotti: {eur(detail.totale_prodotti)}</div>
                  </div>
                )}
                {(detail.altre_voci || []).length > 0 && (
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-primary mb-1.5">Altre voci / servizi</div>
                    <div className="overflow-x-auto rounded-lg border border-border"><table className="w-full text-sm">
                      <thead><tr className="text-xs uppercase text-muted-foreground bg-secondary/60 border-b border-border"><th className="text-left py-2 px-3">Tipologia</th><th className="text-left py-2 pr-2">Descrizione</th><th className="text-right py-2 px-1">Q.tà</th><th className="text-left py-2 px-1">UM</th><th className="text-right py-2 px-3">Costo</th></tr></thead>
                      <tbody>{detail.altre_voci.map((r, i) => <tr key={i} className="border-b border-border/40"><td className="py-1.5 px-3">{r.tipologia}</td><td className="py-1.5 pr-2 text-muted-foreground">{r.descrizione}</td><td className="py-1.5 px-1 text-right">{num(r.quantita, 2)}</td><td className="py-1.5 px-1">{r.um || "—"}</td><td className="py-1.5 px-3 text-right font-medium">{eur(r.costo_totale)}</td></tr>)}</tbody>
                    </table></div>
                  </div>
                )}
                {(detail.superfici || []).length > 0 && (
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-primary mb-1.5">Superfici di installazione</div>
                    <div className="grid sm:grid-cols-2 gap-2">{detail.superfici.map((s, i) => <div key={i} className="rounded-lg border border-border p-3 text-sm"><div className="font-medium">{s.nome || "Superficie"}</div><div className="text-xs text-muted-foreground">{s.tipologia_installazione}{s.orientamento ? ` · Orientamento ${s.orientamento}` : ""}</div></div>)}</div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Vista 2: Lead Outlook ---------------- */
function LeadOutlook() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/preventivi-outlook", { params: q ? { q } : {} }); setItems(data.items || []); setTotal(data.total || 0); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [q]);
  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <p className="text-xs text-muted-foreground mb-3">{total} proposte "chiavi in mano" generate dai lead nel funnel</p>
      <div className="relative mb-4 max-w-md"><Search className="h-4 w-4 absolute left-3 top-2.5 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca cliente o codice proposta…" className="pl-9" data-testid="outlook-search" /></div>
      {loading ? <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        : items.length === 0 ? <div className="rounded-lg border border-dashed border-border py-16 text-center text-muted-foreground"><FileText className="h-8 w-8 mx-auto mb-3 opacity-40" /><p className="text-sm">Nessuna proposta Outlook. Vengono generate dai lead con un preventivo configurato.</p></div>
        : (
          <div className="overflow-x-auto rounded-lg border border-border">
            <Table>
              <TableHeader><TableRow className="border-border"><TableHead>Codice</TableHead><TableHead>Cliente</TableHead><TableHead>Fase</TableHead><TableHead className="text-right">Potenza</TableHead><TableHead className="text-right">Prezzo (IVA incl.)</TableHead><TableHead className="w-32 text-right">Azioni</TableHead></TableRow></TableHeader>
              <TableBody>
                {items.map((p) => (
                  <TableRow key={p.id} className="border-border cursor-pointer" onClick={() => navigate(`/leads/${p.id}`)} data-testid={`outlook-row-${p.id}`}>
                    <TableCell className="font-medium">{p.numero || "—"}</TableCell>
                    <TableCell>{p.cliente}{p.city && <span className="text-muted-foreground text-xs"> · {p.city}</span>}</TableCell>
                    <TableCell><StatoBadge stato={STAGE_LABELS[p.stage] || p.stage} /></TableCell>
                    <TableCell className="text-right">{num(p.pv_kwp, 2)} kWp</TableCell>
                    <TableCell className="text-right font-medium">{eur(p.prezzo)}</TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="sm" onClick={() => navigate(`/leads/${p.id}`)} data-testid={`outlook-open-${p.id}`}><ExternalLink className="h-4 w-4" /></Button>
                      {p.has_link && <Button variant="ghost" size="sm" onClick={() => navigate(`/leads/${p.id}/print`)} data-testid={`outlook-pdf-${p.id}`}><FileDown className="h-4 w-4" /></Button>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
    </div>
  );
}

/* ---------------- Vista 3: Preventivi materiale ---------------- */
function PreventiviMateriale() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/preventivi", { params: q ? { q } : {} }); setItems(data.items || []); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [q]);
  useEffect(() => { load(); }, [load]);

  const openPdf = async (p) => {
    try {
      const res = await api.get(`/preventivi/${p.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `Preventivo_${(p.numero || p.id).replace(/\//g, "-")}.pdf`; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) { toast.error("Emetti prima il preventivo per generare il PDF"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <p className="text-xs text-muted-foreground">{items.length} preventivi di vendita materiale + servizi (Div. Impianti)</p>
        <Button size="sm" onClick={() => navigate("/preventivatore")} data-testid="mat-new-btn" className="font-semibold"><Calculator className="h-4 w-4 mr-1.5" /> Apri Preventivatore</Button>
      </div>
      <div className="relative mb-4 max-w-md"><Search className="h-4 w-4 absolute left-3 top-2.5 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca cliente o numero…" className="pl-9" data-testid="mat-search" /></div>
      {loading ? <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        : items.length === 0 ? <div className="rounded-lg border border-dashed border-border py-16 text-center text-muted-foreground"><Calculator className="h-8 w-8 mx-auto mb-3 opacity-40" /><p className="text-sm">Nessun preventivo materiale. Creane uno dal <b>Preventivatore</b>.</p></div>
        : (
          <div className="overflow-x-auto rounded-lg border border-border">
            <Table>
              <TableHeader><TableRow className="border-border"><TableHead>Numero</TableHead><TableHead>Cliente</TableHead><TableHead>Stato</TableHead><TableHead className="text-right">Potenza</TableHead><TableHead className="text-right">Totale (IVA incl.)</TableHead><TableHead className="w-20 text-right">PDF</TableHead></TableRow></TableHeader>
              <TableBody>
                {items.map((p) => (
                  <TableRow key={p.id} className="border-border cursor-pointer" onClick={() => navigate("/preventivatore")} data-testid={`mat-row-${p.id}`}>
                    <TableCell className="font-medium">{p.numero || "(bozza)"}</TableCell>
                    <TableCell>{p.cliente?.company_name || "—"}</TableCell>
                    <TableCell><StatoBadge stato={MAT_STATUS[p.status] || p.status} /></TableCell>
                    <TableCell className="text-right">{num(p.totals?.potenza, 2)} kWp</TableCell>
                    <TableCell className="text-right font-medium">{eur(p.totals?.totale)}</TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}><Button variant="ghost" size="sm" onClick={() => openPdf(p)} data-testid={`mat-pdf-${p.id}`}><FileDown className="h-4 w-4" /></Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
    </div>
  );
}

export default function FierPreventivi() {
  return (
    <div data-testid="preventivi-hub-page">
      <header className="sticky top-14 lg:top-0 z-10 border-b border-border bg-background px-4 lg:px-8 py-3">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-primary/10 p-2"><Receipt className="h-5 w-5 text-primary" strokeWidth={1.5} /></div>
          <div><h1 className="text-xl font-heading font-bold">Preventivi</h1><p className="text-xs text-muted-foreground">Archivio FIER · Lead Outlook · Preventivi materiale — modello e condizioni condivisi</p></div>
        </div>
      </header>
      <div className="p-4 lg:p-8">
        <Tabs defaultValue="fier">
          <TabsList className="mb-5">
            <TabsTrigger value="fier" data-testid="tab-fier"><Archive className="h-4 w-4 mr-1.5" /> Archivio FIER</TabsTrigger>
            <TabsTrigger value="outlook" data-testid="tab-outlook"><FileText className="h-4 w-4 mr-1.5" /> Lead Outlook</TabsTrigger>
            <TabsTrigger value="materiale" data-testid="tab-materiale"><Calculator className="h-4 w-4 mr-1.5" /> Preventivi materiale</TabsTrigger>
          </TabsList>
          <TabsContent value="fier"><ArchivioFier /></TabsContent>
          <TabsContent value="outlook"><LeadOutlook /></TabsContent>
          <TabsContent value="materiale"><PreventiviMateriale /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
